import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { styled } from '@linaria/react';
import { Accordion } from '@vio/atlas/Accordion';
import { useDeviceLayout } from '@vio/atlas/context/deviceLayout';
import { BookingOptionsFilter } from 'components/Filters/BookingOptionsFilter';
import { ColumnType, FacetFilter, RenderType } from 'components/Filters/FacetFilter';
import { filterMessages } from 'components/Filters/filterMessages';
import { MealPlansFilter } from 'components/Filters/MealPlansFilter';
import { PopularFilters } from 'components/Filters/PopularFilters/PopularFilters';
import { PriceFilter } from 'components/Filters/PriceFilter/PriceFilter';
import { PropertyTypeFilter } from 'components/Filters/PropertyTypeFilter/PropertyTypeFilter';
import { ReviewScoreFilter } from 'components/Filters/ReviewScoreFilter/ReviewScoreFilter';
import { StarRatingFilter } from 'components/Filters/StarRatingFilter';
import { buildDataId, getFiltersLabel } from 'components/Filters/utils';
import { PROPERTY_GROUPS, PROPERTY_TYPE_LIMIT } from 'components/FiltersPopovers/PropertyTypeFilterPopover';
import { THEMES_GROUPS } from 'components/FiltersPopovers/ThemesFilterPopover/ThemesFilterPopover';
import { SortByContent } from 'components/SortBy';
import { useFiltersForm } from 'hooks/useFiltersForm';
import { getAllTaxesIncluded } from 'modules/meta/selectors';
import { getFacets } from 'modules/sapiSearch/selectors';
import { searchParamToFacet } from 'modules/search/constants';
import { getIsCheckInAtLeastTwoDaysAway, getListOfValues } from 'modules/search/selectors';
import { FilterUrlParams } from 'modules/search/types';
import { FACILITY_GROUPS } from 'utils/facilities';
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage';
import { FilterOverlayItem } from '../FilterOverlayItem';
import { FilterOverlaySummary } from '../FilterOverlaySummary/FilterOverlaySummary';
type Props = Pick<ReturnType<typeof useFiltersForm>, 'getFilters' | 'getAppliedFilters' | 'toggleMultiFilter' | 'toggleFilters' | 'getFiltersCount' | 'setMultiFilters' | 'removeMultiFilters'>;
const facilitiesKey = searchParamToFacet.facilities.param;
const themesKey = searchParamToFacet.themes.param;
const propertyTypesKey = searchParamToFacet.propertyTypes.param;
const popularKey = 'popular';
const Wrapper = /*#__PURE__*/styled('div')({
  name: "Wrapper",
  class: "w1uup5im",
  propsAsIs: false
});
const FacetListWrapper = /*#__PURE__*/styled('div')({
  name: "FacetListWrapper",
  class: "fq8nkqa",
  propsAsIs: false
});
export const FiltersOverlayContent: React.FC<Props> = ({
  getAppliedFilters,
  getFilters,
  toggleFilters,
  toggleMultiFilter,
  getFiltersCount,
  setMultiFilters,
  removeMultiFilters
}) => {
  const {
    formatMessage
  } = useIntl();
  const {
    isMobile,
    isDesktop
  } = useDeviceLayout();
  const filters = getFilters();
  const appliedFilters = getAppliedFilters();
  const isCheckInAtLeastTwoDaysAway = useSelector(getIsCheckInAtLeastTwoDaysAway);
  const facets = useSelector(getFacets) ?? {};
  const listOfValues = useSelector(getListOfValues);
  const includeTaxes = useSelector(getAllTaxesIncluded);
  const handleSingleFilterChange = useCallback((value: FilterUrlParams) => {
    toggleFilters({
      ...value
    });
  }, [toggleFilters]);
  const handleMultiFilterChange = useCallback((filterKey: keyof FilterUrlParams, filterValue: string | null | undefined) => {
    toggleMultiFilter(filterKey, filterValue);
  }, [toggleMultiFilter]);
  const getFacilityLabel = useCallback((facetId: string) => getFiltersLabel('Facility', facetId, listOfValues), [listOfValues]);
  const getPropertyTypeLabel = useCallback((facetId: string) => getFiltersLabel('PropertyType', facetId, listOfValues), [listOfValues]);
  const getThemeLabel = useCallback((facetId: string) => getFiltersLabel('Theme', facetId, listOfValues), [listOfValues]);
  const facetFilterColumns = isMobile ? ColumnType.One : ColumnType.Two;
  const facetFilterLimit = isMobile ? 3 : 6;
  const hasSelectedFilters = !isMobile && getFiltersCount() > 0;
  return <Wrapper>
      {/* Selected filters   */}
      {hasSelectedFilters && <FilterOverlayItem title={<FormattedMessageWrapper id="selectedFilters" defaultMessage="Selected Filters" />}>
          <FilterOverlaySummary getFilters={getFilters} toggleMultiFilter={toggleMultiFilter} toggleFilters={toggleFilters} />
        </FilterOverlayItem>}

      {/* Sort */}
      <FilterOverlayItem title={<FormattedMessageWrapper id="sortBy" defaultMessage="Sort by" />}>
        <SortByContent sortField={filters.sortField} sortOrder={filters.sortOrder} onChange={handleSingleFilterChange} />
      </FilterOverlayItem>
      {/* Popular filters */}
      <FilterOverlayItem dataId={buildDataId(popularKey)} title={formatMessage(filterMessages.popular)}>
        <PopularFilters filters={filters} handleSingleFilterChange={handleSingleFilterChange} toggleMultiFilter={toggleMultiFilter} />
      </FilterOverlayItem>
      {/* Booking options */}
      {isCheckInAtLeastTwoDaysAway && <FilterOverlayItem title={formatMessage(filterMessages.bookingOptions)}>
          <BookingOptionsFilter filters={filters} handleSingleFilterChange={handleSingleFilterChange} />
        </FilterOverlayItem>}
      {/* Price */}
      <FilterOverlayItem title={formatMessage(filterMessages.nightlyPrice)} subtitle={includeTaxes && formatMessage(filterMessages.priceSubtitle)}>
        <PriceFilter priceMin={filters.priceMin} priceMax={filters.priceMax} onChange={handleSingleFilterChange} />
      </FilterOverlayItem>
      {/* Amenities */}
      <FilterOverlayItem dataId={buildDataId(facilitiesKey)} title={formatMessage(filterMessages.amenities)} hasShowMore>
        <FacetFilter facets={facets[searchParamToFacet.facilities.facet]} facetGroups={FACILITY_GROUPS} filters={filters[facilitiesKey]} appliedFilters={appliedFilters[facilitiesKey]} searchParamName={facilitiesKey} getLabel={getFacilityLabel} columns={facetFilterColumns} limit={6} onChange={toggleMultiFilter} renderType={RenderType.Card} />
      </FilterOverlayItem>
      {/* Meal plans */}
      <FilterOverlayItem title={formatMessage(filterMessages.mealPlans)}>
        <MealPlansFilter amenities={filters.amenities} onChange={toggleMultiFilter} />
      </FilterOverlayItem>
      {/* Review Score */}
      <FilterOverlayItem title={formatMessage(filterMessages.reviewScore)}>
        <ReviewScoreFilter value={filters.reviewScore} onChange={handleSingleFilterChange} />
      </FilterOverlayItem>
      {/* Star rating */}
      <FilterOverlayItem title={formatMessage(filterMessages.hotelStarClass)}>
        <StarRatingFilter value={filters.starRatings} onChange={handleMultiFilterChange} />
      </FilterOverlayItem>
      {/* Property type */}
      <FilterOverlayItem title={formatMessage(filterMessages.propertyType)} hasShowMore>
        <PropertyTypeFilter facets={facets[searchParamToFacet.propertyTypes.facet]} facetGroups={PROPERTY_GROUPS} filters={filters[propertyTypesKey]} appliedFilters={appliedFilters[propertyTypesKey]} searchParamName={propertyTypesKey} getLabel={getPropertyTypeLabel} columns={facetFilterColumns} limit={PROPERTY_TYPE_LIMIT} onChange={toggleMultiFilter} setMultiFilters={setMultiFilters} removeMultiFilters={removeMultiFilters} showMoreRenderType="Link" />
      </FilterOverlayItem>

      {/* Themes */}
      <Accordion isOpenByDefault={false} header={formatMessage(filterMessages.travelerExperience)} dataId={buildDataId(themesKey)} size="xl" hasSidePadding={isDesktop}>
        <FacetListWrapper>
          <FacetFilter facets={facets[searchParamToFacet.themes.facet]} facetGroups={THEMES_GROUPS} filters={filters[themesKey]} appliedFilters={appliedFilters[themesKey]} searchParamName={themesKey} getLabel={getThemeLabel} columns={facetFilterColumns} limit={facetFilterLimit} onChange={toggleMultiFilter} />
        </FacetListWrapper>
      </Accordion>
    </Wrapper>;
};

require("./FiltersOverlayContent.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./FiltersOverlayContent.tsx");